import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { useRbac } from '../../context/RbacContext';
import { useAuth } from '../../context/AuthContext';
import { FuturisticLoader } from '../common/FuturisticLoader';
import { RoleName } from '../../types/auth';

interface RoleRouteProps {
  children: React.ReactNode;
  allowedRoles: RoleName[];
}

const getRoleDashboard = (role: RoleName): string => {
  switch (role) {
    case 'admin':
      return '/admin/dashboard';
    case 'manager':
      return '/manager/dashboard';
    case 'worker':
      return '/worker/dashboard';
    case 'customer':
      return '/customer-portal';
    default:
      return '/auth/login';
  }
};

export const RoleRoute: React.FC<RoleRouteProps> = ({ children, allowedRoles }) => {
  const { hasRole, roles, loading: rbacLoading } = useRbac();
  const { isAuthenticated, loading: authLoading } = useAuth();

  if (authLoading || rbacLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <FuturisticLoader message="Loading Portal" showProgress={false} size="medium" />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }

  const hasAllowedRole = allowedRoles.some((role) => hasRole(role));

  if (!hasAllowedRole) {
    const userRole = roles[0] as RoleName | undefined;
    const redirectTo = userRole ? getRoleDashboard(userRole) : '/auth/login';
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};
